/**
 * Plan 14 (decision 7): the single source of authored barrel dimensions, shared by the
 * obstacle mesh (P14.2, `actors.ts`) and the debris shards (P14.3, `debris.ts`) so the
 * two parallel lanes cannot drift. All values are in the AUTHORED mesh's units — the
 * obstacle instance matrix scales the mesh's widest part (the hoop OD, 1.0) to the sim's
 * hit interval `2 × toWorldX(halfWidth)` ("what you see is what hits you", plan 09), and
 * the debris pool scales shards as fractions of the same reference so a shard always
 * reads as a piece of THIS barrel at whatever size it exploded at.
 *
 * The barrel is a 1.2 m red-oxide fuel drum: three rolling hoops (the strongest
 * silhouette read on game-ready drum references — research/barrel-destruction-research.md
 * §3), a lid with two bung caps, weathering left to the material, not geometry.
 */

/** Authored units: the widest part of the mesh spans exactly 1.0 (the scale reference). */
export const BARREL_DIMS = {
  /** Ground to lid, authored units (the shipped look is 1.2 m tall). */
  height: 1.2,
  /** Hoop outer diameter — the widest part; the instance scale maps THIS to the hit interval. */
  hoopOuterDiameter: 1.0,
  /** Hoop cross-section height. */
  hoopHeight: 0.05,
  /** Hoop centre heights, ground-relative: bottom chime, mid rolling hoop, top chime. */
  hoopYs: [0.16, 0.6, 1.04] as const,
  /** Body cylinder outer diameter, slightly inset under the hoops (real drums read that way). */
  bodyDiameter: 0.94,
  /** Lid disc diameter (matches the body; the top hoop reads as the lid rim). */
  lidDiameter: 0.94,
  /** The two bung caps on the lid: outer diameter and protruding height. */
  bungDiameter: 0.16,
  bungHeight: 0.05,
  /** Bung centres, x offsets in authored units on the lid (kept clear of centre). */
  bungOffsets: [-0.22, 0.18] as const,
  /** Base red-oxide tint (the plan-09 look, kept); weathering happens in the material. */
  tint: [0.75, 0.24, 0.13] as const,
  /** Debris reference sizes, fractions of the same authored box (P14.3). */
  shard: {
    /** Wall fragment: arc span (radians of the body circle) × height fraction × thickness. */
    wallArc: 1.1,
    wallHeightFraction: 0.34,
    wallThickness: 0.05,
    /** Lid ring: outer = lid, inner hole fraction of the lid radius, flat thickness. */
    lidInnerFraction: 0.55,
    lidThickness: 0.05,
  },
} as const;
